import type { CustomerRow, ProductCodeRow, ProductRow, ProductSerialRow, RowOf } from '@ims/data'
import {
  adToBs,
  BS_MONTHS_EN,
  daysInBsMonth,
  parseMoney,
  parseQty,
  parseScaleBarcode,
  type ScaleReading,
} from '@ims/core'
import { AnchoredList, Button, Input, Select, cx } from '@ims/ui'
import { Plus, Search } from '@ims/ui/icons'
import {
  lazy,
  Suspense,
  useId,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react'
import { useLive, useSettings } from './hooks.ts'
import { changeBsDate } from './lib/dates.ts'
import { money, todayIso } from './lib/format.ts'
import { findSameName, looksLikeCode } from './lib/pick.ts'
import { readScaleConfig } from './lib/scale.ts'
import { PartyCombobox, partyLabel } from './party-picker.tsx'
import { ScanButton, type ScanCart, type ScanResult } from './scanner.tsx'
import { useCan, useRepo } from './workspace.tsx'

// The product form imports this file: loaded when first needed, so the two never
// wait on each other.
const NewProductForm = lazy(() =>
  import('./screens/products.tsx').then((m) => ({ default: m.ProductForm })),
)

/** Reference data used across screens, kept live. */
/**
 * A list page's Add on phones and tablets: a soft button at the end of the search row, so
 * the bottom bar's + stays the one solid button in view. The page header keeps the solid
 * one from 1024px (`max-lg:hidden` on it). Named like the header's, so tests and screen
 * readers find one "Add customer" at any width.
 */
export function PhoneAdd({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="soft"
      icon={<Plus className="size-4" />}
      aria-label={label}
      onClick={onClick}
      className="shrink-0 lg:hidden"
    >
      Add
    </Button>
  )
}

export function useLookups() {
  const live = useLive(
    async (repo) => {
      const [taxRates, units, locations, categories, brands] = await Promise.all([
        repo.list('tax_rates', { orderBy: { column: 'rate_bp', direction: 'desc' }, limit: 100 }),
        repo.list('units', { orderBy: { column: 'name' }, limit: 100 }),
        repo.list('locations', { orderBy: { column: 'name' }, limit: 200 }),
        repo.list('categories', { orderBy: { column: 'name' }, limit: 500 }),
        repo.list('brands', { orderBy: { column: 'name' }, limit: 2000 }),
      ])
      return {
        taxRates: taxRates.rows,
        units: units.rows,
        locations: locations.rows,
        categories: categories.rows,
        brands: brands.rows,
      }
    },
    [],
    ['tax_rates', 'units', 'locations', 'categories', 'brands'],
  )
  return useMemo(() => {
    const d = live.data ?? { taxRates: [], units: [], locations: [], categories: [], brands: [] }
    return {
      ...d,
      loading: live.loading,
      /** The first read finished (lists may still be empty). */
      loaded: live.data !== undefined,
      taxRate: (id: string | null | undefined) => d.taxRates.find((t) => t.id === id) ?? null,
      unit: (id: string | null | undefined) => d.units.find((u) => u.id === id) ?? null,
      location: (id: string | null | undefined) => d.locations.find((l) => l.id === id) ?? null,
      category: (id: string | null | undefined) => d.categories.find((c) => c.id === id) ?? null,
      brand: (id: string | null | undefined) => d.brands.find((b) => b.id === id) ?? null,
      activeLocations: d.locations.filter((l) => l.is_active),
    }
  }, [live.data, live.loading])
}

export type Lookups = ReturnType<typeof useLookups>

/** Text input holding rupees; `onValue` gets paisa (or null while invalid). */
export function MoneyInput({
  paisa,
  onValue,
  prefix,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'prefix'> & {
  paisa: number | null
  onValue: (paisa: number | null) => void
  /** Currency shown inside the field, e.g. "Rs." */
  prefix?: string
}) {
  const [text, setText] = useState(paisa === null ? '' : (paisa / 100).toFixed(2))
  const [last, setLast] = useState(paisa)
  if (paisa !== last) {
    setLast(paisa)
    let current: number | null
    try {
      current = text === '' ? null : parseMoney(text)
    } catch {
      current = null
    }
    if (current !== paisa) setText(paisa === null ? '' : (paisa / 100).toFixed(2))
  }
  const input = (
    <Input
      inputMode="decimal"
      className={cx(prefix && 'pl-10', className)}
      value={text}
      onChange={(e) => {
        setText(e.target.value)
        try {
          onValue(e.target.value.trim() === '' ? null : parseMoney(e.target.value))
        } catch {
          onValue(null)
        }
      }}
      {...rest}
    />
  )
  if (!prefix) return input
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted"
      >
        {prefix}
      </span>
      {input}
    </div>
  )
}

/** Quantity input in units; `onValue` gets milli-units (or null while invalid). */
export function QtyInput({
  milli,
  allowsDecimal = true,
  onValue,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  milli: number | null
  allowsDecimal?: boolean
  onValue: (milli: number | null) => void
}) {
  const fmt = (m: number | null) => (m === null ? '' : String(m / 1000))
  const [text, setText] = useState(fmt(milli))
  const [last, setLast] = useState(milli)
  if (milli !== last) {
    setLast(milli)
    let current: number | null
    try {
      current = text === '' ? null : parseQty(text, allowsDecimal)
    } catch {
      current = null
    }
    if (current !== milli) setText(fmt(milli))
  }
  return (
    <Input
      inputMode={allowsDecimal ? 'decimal' : 'numeric'}
      value={text}
      onChange={(e) => {
        setText(e.target.value)
        try {
          onValue(e.target.value.trim() === '' ? null : parseQty(e.target.value, allowsDecimal))
        } catch {
          onValue(null)
        }
      }}
      {...rest}
    />
  )
}

/**
 * Search box for products. Enter with an exact barcode/SKU (scanners type the
 * code and press Enter) picks it immediately; otherwise shows matches.
 */
export function ProductPicker({
  onPick,
  placeholder = 'Scan a barcode or search products…',
  autoFocus,
  activeOnly = true,
  forSale,
  inputRef,
  scan = 'single',
  scanCart,
  addNew,
}: {
  /**
   * `pack` is set when a pack barcode ("Box of 12") was scanned, `serial` when
   * a unit's serial number (IMEI) was, `scale` when a weighing-scale sticker was.
   */
  onPick: (
    product: ProductRow,
    pack: ProductCodeRow | null,
    serial?: ProductSerialRow | null,
    scale?: ScaleReading | null,
  ) => void
  placeholder?: string
  autoFocus?: boolean
  activeOnly?: boolean
  /** Selling: only products sold to customers (a restaurant's ingredients are left out). */
  forSale?: boolean
  inputRef?: Ref<HTMLInputElement>
  /** The camera button: read one code and close, or keep reading (a counter). */
  scan?: 'single' | 'continuous'
  /** What has been scanned so far, listed under the camera while it reads. */
  scanCart?: ScanCart
  /**
   * A name nobody has can be added on the spot (docs/plans/add-here.md): the
   * list ends with "Add … as a new product", or "ingredient" in a recipe.
   */
  addNew?: 'product' | 'ingredient'
}) {
  const [term, setTerm] = useState('')
  const [highlight, setHighlight] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const results =
    useLive(
      (repo) =>
        term.trim()
          ? repo.products.search(term, { limit: 8, activeOnly, scope: 'sellable', forSale })
          : Promise.resolve({ rows: [], total: 0 }),
      [term, activeOnly, forSale],
      ['products'],
    ).data?.rows ?? []
  const repo = useRepo()
  const settings = useSettings()
  const canAdd = useCan('staff')
  // A scanned code or a searched name no product has, being added as a new product.
  const [adding, setAdding] = useState<{ barcode?: string; name?: string } | null>(null)
  // The text Enter last looked up and did not find: a second Enter adds it.
  const [missed, setMissed] = useState<string | null>(null)
  const typed = term.trim()
  const offerNew = !!addNew && canAdd && typed !== '' && !findSameName(results, typed)
  const startNew = () => {
    setAdding(looksLikeCode(typed) ? { barcode: typed } : { name: typed })
    setTerm('')
    setHighlight(0)
    setMissed(null)
  }

  /** A weighing-scale sticker: the product with that item code, and the weight or price. */
  const scaleMatch = async (code: string) => {
    // A scan right after the page opens must not miss because settings are still loading.
    const current = settings === undefined ? await repo.settings() : settings
    const config = readScaleConfig(current?.scale_barcode)
    const reading = config ? parseScaleBarcode(code, config) : null
    if (!reading) return null
    const product = (
      await repo.list('products', { where: { plu_code: reading.plu }, limit: 5 })
    ).rows.find((p) => !p.deleted_at && p.kind !== 'group')
    return product ? { product, reading } : null
  }

  const pick = (product: ProductRow) => {
    onPick(product, null)
    setTerm('')
    setHighlight(0)
  }

  /**
   * A whole code, typed and entered or read by a scanner or the camera: picks its
   * product (with its pack, serial or scale reading). Returns the product picked, or
   * null after putting the text back in the box to fix.
   */
  const submitCode = async (code: string, match?: ProductRow): Promise<ProductRow | null> => {
    const giveBack = () => setTerm((t) => (t === '' ? code : t))
    const found = await repo.products.lookupCode(code)
    // A variant group is never picked: its code lists the variants instead.
    if (found && found.product.kind === 'group') {
      giveBack()
      return null
    }
    if (found) {
      onPick(found.product, found.pack, found.serial ?? null)
      return found.product
    }
    const scaled = await scaleMatch(code)
    if (scaled) {
      onPick(scaled.product, null, null, scaled.reading)
      return scaled.product
    }
    if (match) {
      onPick(match, null)
      return match
    }
    giveBack() // nothing found: give the text back to fix
    return null
  }

  /** A code from the camera: the same lookup, with words for the scanner to show. */
  const onScan = async (code: string): Promise<ScanResult> => {
    const product = await submitCode(code)
    if (product) return { ok: true, message: `Added ${product.name}` }
    // A counter keeps scanning, so the unknown code must not wait in the search box.
    if (scan === 'continuous') setTerm((t) => (t === code ? '' : t))
    return {
      ok: false,
      message: `No product with the code ${code}`,
      unknown: code,
      // Staff may add it on the spot, with the barcode filled in.
      action: canAdd
        ? {
            label: 'Add product',
            run: () => {
              setTerm('')
              setAdding({ barcode: code })
            },
          }
        : undefined,
    }
  }

  const onKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      // One step past the results is the add row.
      setHighlight((h) => Math.min(h + 1, results.length - (offerNew ? 0 : 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      // The add row, reached with the arrows, or Enter again after a miss. A first
      // Enter always looks the text up as a code: a scanner ends every code with one.
      if (offerNew && (results.length === 0 ? missed === term : highlight >= results.length)) {
        startNew()
        return
      }
      // Clear right away: a scanner may type the next code (even the same one)
      // before this lookup finishes.
      const code = term
      const match = results[highlight]
      setTerm('')
      setHighlight(0)
      const found = await submitCode(code, match)
      setMissed(found ? null : code)
    } else if (e.key === 'Escape') {
      setTerm('')
    }
  }

  return (
    <div>
      {/* The box the list hangs from: on a phone the list follows it in the flow. */}
      <div ref={box} className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle"
          aria-hidden
        />
        <input
          ref={inputRef}
          value={term}
          autoFocus={autoFocus}
          onChange={(e) => {
            setTerm(e.target.value)
            setHighlight(0)
          }}
          onKeyDown={(e) => void onKeyDown(e)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full rounded-xl border border-border bg-surface py-2.5 pl-9 pr-11 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <ScanButton
          mode={scan}
          onCode={onScan}
          cart={scanCart}
          className="absolute right-1 top-1/2 -translate-y-1/2"
        />
      </div>
      {typed && (results.length > 0 || offerNew) && (
        <AnchoredList anchor={box}>
          <ul>
            {results.map((p, i) => (
              <li key={p.id}>
                <button
                  type="button"
                  // The search box keeps the focus, so the arrows and Enter go on working.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(p)}
                  className={cx(
                    'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm',
                    i === highlight && 'bg-primary-light',
                  )}
                >
                  <span>
                    <span className="font-medium">{p.name}</span>
                    <span className="ml-2 text-xs text-muted">{p.sku}</span>
                  </span>
                  <span className="tabular shrink-0 whitespace-nowrap text-muted">
                    {money(p.sell_price_paisa)}
                  </span>
                </button>
              </li>
            ))}
            {offerNew && (
              <li className={cx(results.length > 0 && 'mt-1 border-t border-border pt-1')}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(results.length)}
                  onClick={startNew}
                  className={cx(
                    'flex min-h-10 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-primary',
                    (results.length === 0 || highlight >= results.length) && 'bg-primary-light',
                  )}
                >
                  <span
                    aria-hidden
                    className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary/15"
                  >
                    <Plus className="size-3.5" />
                  </span>
                  <span className="min-w-0 truncate">
                    Add “{typed}” as a new {addNew}
                  </span>
                </button>
              </li>
            )}
          </ul>
        </AnchoredList>
      )}
      {typed && results.length === 0 && !offerNew && (
        <AnchoredList anchor={box} className="p-3 text-sm text-muted">
          No products match “{term}”.{addNew && !canAdd ? ' Ask a manager to add it.' : ''}
        </AnchoredList>
      )}
      {adding !== null && (
        <Suspense fallback={null}>
          <NewProductForm
            product={null}
            barcode={adding.barcode}
            name={adding.name}
            ingredient={addNew === 'ingredient'}
            onClose={() => setAdding(null)}
            onSaved={(id) => {
              setAdding(null)
              // The new product is what was being scanned: pick it.
              void repo.get('products', id).then((p) => p && onPick(p, null))
            }}
          />
        </Suspense>
      )}
    </div>
  )
}

export function CustomerSelect({
  value,
  onChange,
  id,
  placeholderLabel = 'Walk-in customer',
}: {
  value: string | null
  onChange: (customer: CustomerRow | null) => void
  id?: string
  placeholderLabel?: string
}) {
  const customers =
    useLive(
      (repo) =>
        repo.list('customers', {
          where: { is_active: true },
          orderBy: { column: 'name' },
          limit: 500,
        }),
      [],
      ['customers'],
    ).data?.rows ?? []
  return (
    <Select
      id={id}
      value={value ?? ''}
      onChange={(e) => onChange(customers.find((c) => c.id === e.target.value) ?? null)}
    >
      <option value="">{placeholderLabel}</option>
      {customers.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
          {c.phone ? ` · ${c.phone}` : ''}
        </option>
      ))}
    </Select>
  )
}

export function customerLabel(c: Pick<CustomerRow, 'name' | 'phone'>): string {
  return partyLabel(c)
}

/**
 * Searchable customer picker for the counter: type a name or phone, pick with
 * ↑/↓ and Enter, or add a new customer. Empty means a walk-in customer.
 */
export function CustomerCombobox({
  id,
  value,
  onChange,
  onAddNew,
  inputRef,
}: {
  id?: string
  value: CustomerRow | null
  onChange: (customer: CustomerRow | null) => void
  /** Called with the typed text when the user asks to add a new customer. */
  onAddNew?: (name: string) => void
  inputRef?: Ref<HTMLInputElement>
}) {
  const customers =
    useLive(
      (repo) =>
        repo.list('customers', {
          where: { is_active: true },
          orderBy: { column: 'name' },
          limit: 5000,
        }),
      [],
      ['customers'],
    ).data?.rows ?? []
  return (
    <PartyCombobox
      id={id}
      rows={customers}
      value={value}
      onChange={onChange}
      onAddNew={onAddNew}
      inputRef={inputRef}
      words={{
        one: 'customer',
        many: 'customers',
        placeholder: 'Walk-in · search name or phone',
        newLabel: 'New customer',
        clearLabel: 'Walk-in customer',
      }}
    />
  )
}

export function LocationSelect({
  locations,
  value,
  onChange,
  id,
}: {
  locations: RowOf<'locations'>[]
  value: string | null
  onChange: (id: string) => void
  id?: string
}) {
  return (
    <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      {locations.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
        </option>
      ))}
    </Select>
  )
}

/**
 * A date picker in the shop's calendar: the browser's date input for English
 * dates, Year / Month / Day selects for Nepali dates. The value is always an
 * AD date (YYYY-MM-DD), like every date column.
 */
export function DateInput({
  label,
  value,
  onChange,
  calendar,
  className,
}: {
  label: string
  value: string
  onChange: (iso: string) => void
  calendar: 'bs' | 'ad'
  className?: string
}) {
  const id = useId()
  if (calendar === 'ad') {
    return (
      <div className={className}>
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-muted">
          {label}
        </label>
        <Input
          id={id}
          type="date"
          value={value}
          onChange={(e) => /^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && onChange(e.target.value)}
        />
      </div>
    )
  }
  const bs = adToBs(value)
  const thisYear = adToBs(todayIso()).year
  const years = Array.from({ length: 11 }, (_, i) => thisYear - 10 + i)
  if (!years.includes(bs.year)) years.unshift(bs.year)
  const days = daysInBsMonth(bs.year, bs.month)
  return (
    <fieldset className={className}>
      <legend className="mb-1 block text-sm font-medium text-muted">{label}</legend>
      {/* Select is w-full, so the widths come from the grid. */}
      <div className="grid grid-cols-[5rem_minmax(6rem,1fr)_6.75rem] gap-1.5">
        <Select
          aria-label={`${label}: day`}
          value={bs.day}
          onChange={(e) => onChange(changeBsDate(value, { day: Number(e.target.value) }))}
        >
          {Array.from({ length: days }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </Select>
        <Select
          aria-label={`${label}: month`}
          value={bs.month}
          onChange={(e) => onChange(changeBsDate(value, { month: Number(e.target.value) }))}
        >
          {BS_MONTHS_EN.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select
          aria-label={`${label}: year`}
          value={bs.year}
          onChange={(e) => onChange(changeBsDate(value, { year: Number(e.target.value) }))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>
    </fieldset>
  )
}
